/**
 * Markets layout (#1953): tiles or compact rows, remembered per browser, plus the pure helpers of the inline market
 * panel. No React.
 */
import type { MouseEvent } from 'react';

export type CatalogLayout = 'tiles' | 'compact';
export const CATALOG_LAYOUTS: readonly CatalogLayout[] = ['tiles', 'compact'];

export const LAYOUT_STORAGE_KEY = 'outfox.catalog.layout';

/** Search param of the outcome open in the inline panel (`/?m=123`). */
export const PANEL_PARAM = 'm';

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStore(): Store | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function loadLayout(store: Store | undefined = defaultStore()): CatalogLayout {
  try {
    return store?.getItem(LAYOUT_STORAGE_KEY) === 'compact' ? 'compact' : 'tiles';
  } catch {
    return 'tiles';
  }
}

export function saveLayout(layout: CatalogLayout, store: Store | undefined = defaultStore()): void {
  try {
    store?.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
    /* storage unavailable: the choice lasts until the page closes */
  }
}

/**
 * Where the panel goes among `total` grid items so it sits under the row holding the item at `index`: the index just
 * after that row (`cols` items per row). An item that is not listed (`index` < 0) puts the panel first.
 */
export function panelSlot(index: number, cols: number, total: number): number {
  if (index < 0) return 0;
  return Math.min((Math.floor(index / cols) + 1) * cols, total);
}

/** A plain left click. New-tab clicks (ctrl, cmd, shift, middle button) keep the link's normal behaviour. */
export function isPlainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/** onClick of an outcome link that opens the inline panel instead of the market page. */
export function panelClick(outcome: number, onSelect: ((outcome: number) => void) | undefined) {
  return (e: MouseEvent) => {
    if (!onSelect || !isPlainClick(e)) return;
    e.preventDefault();
    onSelect(outcome);
  };
}
